import { clamp } from '@/core/rng';
import { processViability } from './leverage';
import type { AnchorState } from './anchor';
import type { EventBus } from './events';

/**
 * Aura — continuous low-intensity influence, and the other half of a build
 * axis that until now had only one half.
 *
 * Law 31: **MANUAL COMPRESSES CAUSALITY; AURA ACCUMULATES IT.** Every divine
 * effect in this world has been an event: a cast writes a deviation once and
 * the land forgets it on an eighteen-day half-life. That is the *manual* half.
 * A god could not choose to be the other kind, so half of `DESIGN.md` §7's
 * Touch/Aura tension did not exist as a thing a player could do.
 *
 * ## The Anchor is the carrier, and that is not a compromise
 *
 * `ROADMAP.md` §16 already named it: *an aura is an anchor that WORKS*. The
 * Anchor persists, holds Resonance, prices reach, and already decays on the
 * day grid. But there is a harder reason than convenience. D §13.4 also lets
 * Presence generate an Aura — and a presence-carried aura would require the
 * simulation to read where the player is standing, continuously, forever.
 * That is precisely the coupling law 1 forbids: nothing in the world may read
 * the camera. An Anchor is a *place the god committed to*, written into the
 * world by an act; a presence is where somebody happens to be looking. Only
 * one of those two can be allowed to change the land.
 *
 * So: auras hang on anchors. Sacred geography (D §8.4 rank III) later lets a
 * network of them behave as one, and that is a change to which anchors are
 * lit, not to what carries the light.
 *
 * ## What it does, and why these numbers
 *
 * Measured first (`tools/aura.ts`), because the whole design lives in the
 * steady state: a daily stamp against an exponential decay converges on
 * `k / (1 − 2^(−1/18))` = **26.4 k**, and where it converges is the mechanic.
 *
 *     manual Strengthen  peaks 0.600, is 0.189 after a month, gone within a year
 *     aura at 1/55 a day peaks 0.288, and is still 0.288 in a century
 *
 * So an aura holds, for ever, 48% of what a manual cast reaches for a
 * fortnight — and half its ceiling arrives in about two world weeks, 97% in a
 * season. An aura is not felt on the day it is lit and is unmistakable by the
 * time the god has been away and come back. That is law 31 as a curve.
 *
 * ## What it costs
 *
 * Upkeep is a drain against regeneration, not a lump: `AURA_UPKEEP_PER_DAY`
 * against `POWER_PER_DAY` of 0.02 means **three auras consume a god entirely**,
 * and one leaves 70% of its regeneration for casting. An aura also equals the
 * price of its own Anchor every hundred world-days, so a stone lit and
 * forgotten is a real and compounding decision. A god that runs dry guts its
 * own auras, oldest anchor first — deterministically, by list order.
 *
 * ## And it is deliberately silent
 *
 * Kindling emits one low event, because it is an act and someone standing
 * there should feel *something*. The daily work emits nothing at all — for
 * determinism's sake (a daily event per aura would flood a 2,000-event ring
 * buffer) but mostly because it is right: a manual cast is a witnessed
 * miracle, and an aura is an **unexplained condition**. Nobody sees a god do
 * anything. The valley is simply good, and has been for as long as anyone
 * remembers. Law 10 wants exactly that gap, and this is the purest form of it
 * the build has.
 *
 * Pure by law 7: no three, no `Math.random`, no wall clock. What an aura does
 * on a given world-day is a function of the day and the ground.
 */

/**
 * How wide. Chosen against measured ground rather than taste: a camp's
 * cleared halo reaches 64–128 m (`tools/settlement.ts`), so 180 m is visibly
 * *more than the village* — the mark a god leaves is bigger than the mark
 * people leave, which is the point of it being divine. At the screenshot
 * gate's 400 m it fills the frame; at 3 km it is still ~126 px.
 */
export const AURA_RADIUS = 180;

/**
 * A day's delivery, as a share of one reference cast. 1/55 puts biomass at
 * 0.0109 a day and therefore a ceiling of 0.288 — see the header. Changing
 * this changes the ceiling by the same factor and nothing else, which is why
 * it is expressed as one number.
 */
export const AURA_DAILY_SHARE = 1 / 55;

/**
 * Power a lit aura drains per world-day — a shade under a third of
 * `POWER_PER_DAY`, so that three auras still consume a god entirely and one
 * still leaves 70% of its regeneration for casting: the ratio the header
 * argues from, kept when regeneration went 0.02 → 0.35 on 2026-08-18 (the
 * five-stone cast economy). Half a stone a day, per aura.
 */
export const AURA_UPKEEP_PER_DAY = 0.105;

/**
 * How often the accrued influence is actually written into the land — and
 * this constant exists because of a real collision, found by measuring rather
 * than by reading.
 *
 * `EnvField` has a noise floor: `NEGLIGIBLE = 0.02`, below which `at()`
 * reports nothing and `sweep()` **deletes the cell**. That floor is correct
 * for what the field was built to hold — spikes from casts, which should
 * stop costing memory once they have faded to nothing anybody can see. But it
 * is exactly wrong for an influence that is *supposed* to be small and
 * persistent: a daily instalment of 0.0055 was swept away every half-hour,
 * for ever, so the first version of this system paid its upkeep and changed
 * nothing at all.
 *
 * Raising the daily rate above the floor would have worked and would have
 * been wrong — 0.02 a day converges on 0.53, which is *stronger* than a
 * manual cast's peak and permanent, and would make Touch pointless. So the
 * rate stays where the probe put it and the **cadence** moves instead: a
 * week's worth arrives at once, comfortably over the floor, and the ceiling
 * is unchanged because the rate is.
 *
 * Seven days is also honest about what an aura is. It is not a thing you feel
 * on the day you light it.
 */
export const AURA_STAMP_DAYS = 7;

/**
 * Which verbs can be held continuously. Strike is a discharge and cannot be
 * sustained; Omen writes nothing into the land and so has nothing to
 * accumulate. What is left is exactly the three that write conditions.
 */
export const AURA_VERBS = ['strengthen', 'weaken', 'rain'] as const;
export type AuraVerb = (typeof AURA_VERBS)[number];

/** What each verb delivers at one full reference cast — mirrors DIVINE_VERBS. */
const AURA_SIGNATURE: Record<AuraVerb, { moisture: number; biomass: number }> = {
  strengthen: { moisture: 0.1, biomass: 0.6 },
  weaken: { moisture: -0.25, biomass: -0.7 },
  rain: { moisture: 0.7, biomass: 0 },
};

/** Lit state, stored on the Anchor so it rides the existing snapshot free. */
export interface AuraState {
  verb: AuraVerb;
  /** World-seconds it was kindled. */
  litAt: number;
  /** Total power this aura has spent since it was lit — the god's own ledger. */
  spent: number;
}

/** The narrow view of the world an aura needs. Nothing else is reachable. */
export interface AuraGround {
  /** Climate wetness and what the biome can carry, at a point. */
  soil(x: number, z: number): { rain: number; carrying: number };
  /** Deviation already present, decayed to `now`. */
  deviation(x: number, z: number, now: number): { moisture: number; biomass: number } | null;
  /** Write a day's worth. Returns chunk keys whose appearance changed. */
  stamp(
    x: number, z: number, radius: number,
    d: { moisture?: number; biomass?: number }, at: number,
  ): string[];
}

/** Just enough of a deity to charge it. */
export interface AuraPurse {
  power: number;
}

export type Refusal = 'not-yours' | 'no-such-verb' | 'already' | 'unlit' | 'too-poor';

export interface AuraResult {
  ok: boolean;
  why?: Refusal;
}

const OK: AuraResult = { ok: true };
const no = (why: Refusal): AuraResult => ({ ok: false, why });

export class AuraSystem {
  constructor(
    private readonly ground: AuraGround,
    private readonly anchors: () => readonly AnchorState[],
    private readonly god: (id: string) => AuraPurse | undefined,
    private readonly events: EventBus,
    /**
     * What this god's Godhood does to the price of holding a place.
     *
     * Injected rather than imported, exactly as `Simulation.mod` is: nothing
     * under `src/sim/` may depend on `godhood/`, and a rival god acting
     * through the same system has to get its own answer. Defaults to 1, so
     * every existing caller — and `test/aura.ts` — is unaffected.
     */
    private readonly mod: (godId: string, variable: string) => number = () => 1,
  ) {}

  /**
   * What a day of holding this place costs THIS god.
   *
   * `aura.upkeep` is the Epic *Where I Am, The World Changes*, and it has to
   * be asked in all three places or it is worse than not wired: `kindle`
   * refuses under one day's upkeep, `advance` gutters an aura under one day's
   * upkeep, and `advance` charges it. A discount that moved only the charge
   * would produce an aura a god can afford to *run* and not to *light* — a
   * purchase that reads as broken rather than as generous.
   */
  private upkeep(godId: string): number {
    return AURA_UPKEEP_PER_DAY * this.mod(godId, 'aura.upkeep');
  }

  /** Every lit anchor, in the world's own order. */
  lit(): readonly AnchorState[] {
    return this.anchors().filter((a) => a.aura);
  }

  /**
   * Light one. Only the god whose hand placed the stone may do it — an aura
   * is that deity's presence continuing in a place, and a rival lighting your
   * anchor would make it a territory mechanic, which the Anchor deliberately
   * is not.
   *
   * It costs nothing to kindle. The cost is that it keeps costing.
   */
  kindle(a: AnchorState, verb: AuraVerb, godId: string, now: number): AuraResult {
    if (a.godId !== godId) return no('not-yours');
    if (!AURA_VERBS.includes(verb)) return no('no-such-verb');
    if (a.aura && a.aura.verb === verb) return no('already');
    const purse = this.god(godId);
    if (!purse || purse.power < this.upkeep(godId)) return no('too-poor');

    a.aura = { verb, litAt: now, spent: 0 };
    // One event, low and wide: somebody standing here feels the place change
    // its mind. What follows is silent — see the header.
    this.events.emit({
      type: 'divine_aura', time: now, x: a.x, z: a.z,
      radius: AURA_RADIUS, magnitude: 1, salience: 0.4,
      cause: { kind: 'divine', godId },
    });
    return OK;
  }

  /** Put one out. Free, immediate, and emits nothing: stopping is not an act. */
  douse(a: AnchorState): AuraResult {
    if (!a.aura) return no('unlit');
    a.aura = undefined;
    return OK;
  }

  /**
   * One world-day of every lit aura. Called from the day boundary, after
   * regeneration and anchor decay, so a god pays for its auras out of the
   * same day's income.
   *
   * Deterministic by construction: it reads the anchors in list order (stable
   * across save and restore), takes no randomness, and what it writes depends
   * only on the ground and the day. Returns the chunk keys that changed.
   */
  advance(from: number, to: number): string[] {
    const dirty = new Set<string>();
    // A week boundary in *absolute* world time, never an offset from when the
    // aura was lit — so advancing a year in one call and in 365 calls cross
    // the same boundaries, which is the whole of law 7.
    const week = AURA_STAMP_DAYS * 86400;
    const writes = Math.floor(to / week) !== Math.floor(from / week);

    for (const a of this.anchors()) {
      const aura = a.aura;
      if (!aura) continue;

      const purse = this.god(a.godId);
      // a god that has left the world cannot hold a place in it
      if (!purse) { a.aura = undefined; continue; }

      const day = this.upkeep(a.godId);
      if (purse.power < day) {
        // it guts out rather than going into debt, and that is worth a word
        // to anyone near: the place stops being strange
        a.aura = undefined;
        /**
         * Salience 0.3 is UNDER `SALIENT` (0.35, `mortals/band.ts`), so
         * `worthRemembering` drops this before a single person can form a
         * memory of it — deliberately. An aura is an unexplained condition,
         * and a condition quietly ceasing is the least witnessable thing in
         * the game: there is no flash, nothing moves, the ground simply stops
         * being strange. Nobody should carry that.
         *
         * The consequence is that `hud.ts`'s `FADED` entry for this type can
         * never be reached — a faded line is the fallback for a mortal's
         * memory id, and no mortal can hold one. It is kept rather than
         * deleted because this constant is the only thing making it dead, and
         * a future ruling that mortals DO notice a place going quiet would
         * need it back. The reader that does fire is god-side, in
         * `main.ts`'s event walk: the one party who needs to know is told.
         */
        this.events.emit({
          type: 'divine_aura_spent', time: to, x: a.x, z: a.z,
          radius: AURA_RADIUS, magnitude: 1, salience: 0.3,
          cause: { kind: 'divine', godId: a.godId },
        });
        continue;
      }

      purse.power = clamp(purse.power - day, 0, 1);
      aura.spent += day;

      // it pays every day and writes every seventh — see AURA_STAMP_DAYS
      if (!writes) continue;

      // the same leverage law a cast obeys: you cannot strengthen a process
      // that is not happening, and an aura is not exempt for being patient
      const { rain, carrying } = this.ground.soil(a.x, a.z);
      const dev = this.ground.deviation(a.x, a.z, to);
      const spec = AURA_SIGNATURE[aura.verb];
      /**
       * An aura obeys every law a cast obeys, so it reads the same Godhood.
       * Four variables, and they are four because an aura has four handles a
       * cast does not share: how much it does in a day (`aura.strength`), how
       * far that reaches (`aura.radius`), what the whole of it is worth
       * (`aura.work`), and what it costs to hold (`aura.upkeep`, above).
       *
       * `processViability` takes the same two ends of the curve `act` passes
       * it — you cannot strengthen a process that is not happening, and an
       * aura is not exempt for being patient.
       */
      const viability = processViability(aura.verb, {
        rain, biomass: dev?.biomass ?? 0, moisture: dev?.moisture ?? 0,
      }, carrying, {
        high: this.mod(a.godId, 'act.work.highViability'),
        low: this.mod(a.godId, 'act.work.lowViability'),
      });

      const share = AURA_DAILY_SHARE * AURA_STAMP_DAYS
        * this.mod(a.godId, 'aura.strength') * this.mod(a.godId, 'aura.work');
      const radius = AURA_RADIUS * this.mod(a.godId, 'aura.radius');
      for (const k of this.ground.stamp(a.x, a.z, radius, {
        moisture: spec.moisture * share,
        biomass: spec.biomass * share * viability,
      }, to)) dirty.add(k);
    }

    return [...dirty];
  }
}

/**
 * Where a daily stamp of this verb converges, for a reader who wants the
 * number without running the probe. `halfLifeDays` is the field's own
 * forgetting — 18 for biomass, 5 for moisture.
 */
export const auraCeiling = (perDay: number, halfLifeDays: number): number =>
  perDay / (1 - Math.pow(0.5, 1 / halfLifeDays));
